import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators';

/**
 * Servicio para detectar estado de conexión a internet
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private onlineSubject: BehaviorSubject<boolean>;
  public online$: Observable<boolean>;

  constructor() {
    // Estado inicial
    this.onlineSubject = new BehaviorSubject<boolean>(navigator.onLine);
    this.online$ = this.onlineSubject.asObservable();

    // Escuchar eventos de cambio de conexión
    this.initNetworkListener();

    console.log(`🌐 Estado inicial de red: ${navigator.onLine ? 'Online' : 'Offline'}`);
  }

  /**
   * Verifica si hay conexión actualmente (síncrono)
   */
  isOnline(): boolean {
    return this.onlineSubject.value;
  }

  /**
   * Obtiene el estado actual como Observable
   */
  getOnlineStatus(): Observable<boolean> {
    return this.online$;
  }

  /**
   * Verifica conexión con el servidor (ping real)
   */
  async checkServerConnection(url?: string): Promise<boolean> {
    if (!navigator.onLine) {
      return false;
    }

    try {
      const targetUrl = url || 'https://www.google.com/favicon.ico';
      const response = await fetch(targetUrl, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
      });
      return true;
    } catch (error) {
      console.warn('⚠️ Ping al servidor falló:', error);
      return false;
    }
  }

  /**
   * Inicializa listeners de eventos de red
   */
  private initNetworkListener(): void {
    // Combinar eventos online/offline
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$)
      .pipe(debounceTime(100)) // Evitar múltiples eventos rápidos
      .subscribe((isOnline) => {
        console.log(`🌐 Cambio de estado de red: ${isOnline ? 'Online ✅' : 'Offline ❌'}`);
        this.onlineSubject.next(isOnline);
      });
  }

  /**
   * Espera a que haya conexión (útil para retry)
   */
  async waitForConnection(timeoutMs: number = 30000): Promise<boolean> {
    if (this.isOnline()) {
      return true;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        subscription.unsubscribe();
        resolve(false);
      }, timeoutMs);

      const subscription = this.online$.subscribe((isOnline) => {
        if (isOnline) {
          clearTimeout(timeout);
          subscription.unsubscribe();
          resolve(true);
        }
      });
    });
  }
}
